import { useMutation, useQuery } from '@apollo/client'
import { ErrorComponent, ProgressSpinner } from '@medical/components'
import {
  STAFF_CREATE_STAFF,
  STAFF_DELETE_STAFF,
  STAFF_UPDATE_STAFF,
} from '@medical/constant/permissions'
import { checkStaffPermission, onPageChange } from '@medical/libs'
import { useCustom } from '@medical/provider/context'
import queryString from 'query-string'
import React, { useContext } from 'react'
import { connect } from 'react-redux'
import { SocketContext } from '@medical/provider/socket'
import {
  removeProgress,
  setProgressBar,
} from '@medical/provider/store/reducers/progress/progress.action'
import { DELETE_STAFF, STAFFS_CONNECTION2 } from './StaffsList.graphql'
import StaffsListScene from './StaffsListScene'

const StaffsList = ({
  history,
  location: { search },
  progress,
  setProgress,
  removeProgress,
}) => {
  const [{ i18n, popup }] = useCustom()
  const socket = useContext(SocketContext)
  const {
    page = 1,
    rowsPerPage = 20,
    fullname,
    staffGroupId,
    orderBy = 'createdAt_DESC',
  } = queryString.parse(search)
  const first = parseInt(rowsPerPage, 10)
  const skip = (parseInt(page, 10) - 1) * parseInt(rowsPerPage, 10)
  const where = {}
  if (fullname) {
    where.OR = [
      { firstname_contains: fullname },
      { lastname_contains: fullname },
      { firstnameKana_contains: fullname },
      { lastnameKana_contains: fullname },
    ]
  }
  if (staffGroupId) {
    where.staffGroup = { id: staffGroupId }
  }
  const variables = {
    first: first < 0 ? 20 : first,
    skip: skip < 0 ? 0 : skip,
    orderBy,
    where: Object.keys(where).length > 0 ? where : undefined,
  }
  const isUpdatePermitted = checkStaffPermission({
    functionName: STAFF_UPDATE_STAFF,
  })
  const isCreatePermitted = checkStaffPermission({
    functionName: STAFF_CREATE_STAFF,
  })
  const isDeletePermitted = checkStaffPermission({
    functionName: STAFF_DELETE_STAFF,
  })
  const { loading, error, data } = useQuery(STAFFS_CONNECTION2, {
    variables,
    fetchPolicy: 'network-only',
  })

  const [deleteStaff] = useMutation(DELETE_STAFF)

  if (loading) return <ProgressSpinner />
  if (error) return <ErrorComponent error={error} />

  const onSubmit = async ({ staffId }, { setSubmitting, resetForm }) => {
    try {
      await deleteStaff({
        variables: {
          staffId,
        },
        update: store => {
          const staffsData = store.readQuery({
            query: STAFFS_CONNECTION2,
            variables,
          })
          const { staffsConnection } = staffsData
          store.writeQuery({
            query: STAFFS_CONNECTION2,
            data: {
              ...staffsData,
              staffsConnection: {
                ...staffsConnection,
                edges: staffsConnection.edges.filter(
                  edge => edge.node.id !== staffId
                ),
                aggregate: {
                  ...staffsConnection.aggregate,
                  count: staffsConnection.aggregate.count - 1,
                },
              },
            },
            variables,
          })
        },
      })
      popup.success(i18n.t('staff.deleteStaff.submissionSuccess'))
      setSubmitting(false)
      resetForm()
    } catch (error) {
      popup.error(error)
      setSubmitting(false)
      resetForm()
    }
  }

  const {
    staffsConnection: {
      edges,
      aggregate: { count },
    },
    staffGroups,
  } = data

  return (
    <StaffsListScene
      i18n={i18n}
      socket={socket}
      progress={progress}
      setProgress={progress => setProgress(progress)}
      removeProgress={progress => removeProgress(progress)}
      edges={edges}
      page={parseInt(page, 10)}
      rowsPerPage={parseInt(rowsPerPage, 10)}
      fullname={fullname}
      staffGroupId={staffGroupId}
      staffGroups={staffGroups}
      count={count}
      onPageChange={onPageChange(history, search)}
      onSubmit={onSubmit}
      isCreatePermitted={isCreatePermitted}
      isDeletePermitted={isDeletePermitted}
      isUpdatePermitted={isUpdatePermitted}
    />
  )
}

const mapStateToProps = state => ({
  progress: state.progressBarStore.progress,
})

const mapDispatchToProps = dispatch => ({
  setProgress: progress => dispatch(setProgressBar(progress)),
  removeProgress: progress => dispatch(removeProgress(progress)),
})
export default connect(mapStateToProps, mapDispatchToProps)(StaffsList)
